import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fromJS } from 'immutable';

import { addOmniChannelLogEntry } from 'flow/utils/browser-utils';
import {
    getSubscribeToUserOwnedErrandsCountPayload,
    getGoToOmniChannelErrandPayload,
    getAuthenticateUserPayload,
    authenticationDataIsInsufficient,
    dataIsOfTypeUserOwnedOmnichannelErrandsCount,
    dataIsOfTypeUserSessionReady,
    dataIsOfTypeCentionIsReadyToReceiveAuthenticationData,
    dataIsOfTypeAuthenticationError,
    getErrorMessageFromData,
    getErrandsCountFromData,
    getCentionUrl,
} from 'flow/utils/omnichannel-utils';
import {
    setOmniChannelDeepLinkId,
    setUserOwnedOmnichannelErrandsAwaitingActivityCount,
    setOmnichannelIframeReference,
    fetchAuthDataWithoutSettingDataInStore,
} from 'flow/actions/omnichannel-actions';

import {
    OmniMessageEventData,
    OmniPostMessagePayload,
} from 'flow/types/Omnichannel';

const MAX_LOGIN_ATTEMPTS = 5;

const OmniiFrameWrapper = () => {
    const dispatch: any = useAppDispatch();

    const [iFrameRef, setIframeRef] = useState<HTMLIFrameElement>(null);
    const [iFrameIsLoaded, setIFrameIsLoaded] = useState(false);
    const [shouldAuthenticateToCention, setShouldAuthenticateToCention] =
        useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);

    const deepLinkId = useAppSelector((state) =>
        state.omnichannel.get('deepLinkId'),
    );
    const centionUrl = useAppSelector(getCentionUrl);

    useEffect(() => {
        window.addEventListener(
            'message',
            handleIncomingMessageFromCentionClient,
        );
        return () => {
            window.removeEventListener(
                'message',
                handleIncomingMessageFromCentionClient,
            );
        };
    });

    useEffect(() => {
        if (deepLinkId && iFrameIsLoaded) {
            const payload = getGoToOmniChannelErrandPayload(deepLinkId);
            sendPostMessage(payload, centionUrl, iFrameRef);
            dispatch(setOmniChannelDeepLinkId(undefined));
        }
    }, [dispatch, deepLinkId, iFrameIsLoaded, centionUrl, iFrameRef]);

    useEffect(() => {
        if (loginAttempts === MAX_LOGIN_ATTEMPTS) {
            addOmniChannelLogEntry(
                `Max login attempts ${MAX_LOGIN_ATTEMPTS} reached`,
            );
        }
    }, [loginAttempts]);

    useEffect(() => {
        if (
            shouldAuthenticateToCention &&
            iFrameIsLoaded &&
            loginAttempts < MAX_LOGIN_ATTEMPTS
        ) {
            fetchAuthDataWithoutSettingDataInStore()
                .then((data) => {
                    const authenticationData = fromJS(data);
                    if (authenticationDataIsInsufficient(authenticationData)) {
                        // Guard during the transition period.
                        // Peter: "There's probably gonna be some sort of transition period where there are agents who are missing accessKey"
                        addOmniChannelLogEntry(
                            `Authentication data not sufficient for SSO: ${authenticationData.toJS()}`,
                        );
                        return;
                    }

                    const accessKeyId = authenticationData.get('accessKeyId');
                    const timestamp = authenticationData.get('timestamp');
                    const workspace = authenticationData.get('workspace');
                    const signature = authenticationData.get('signature');

                    const payload = getAuthenticateUserPayload(
                        accessKeyId,
                        timestamp,
                        workspace,
                        signature,
                    );
                    sendPostMessage(payload, centionUrl, iFrameRef);
                    setShouldAuthenticateToCention(false);
                })
                .finally(() => {
                    setLoginAttempts(loginAttempts + 1);
                });
        }
    }, [
        dispatch,
        shouldAuthenticateToCention,
        iFrameIsLoaded,
        centionUrl,
        iFrameRef,
        loginAttempts,
    ]);

    const onAuthenticationError = (data: OmniMessageEventData) => {
        const errorMessage = getErrorMessageFromData(data);
        console.warn(`Error authenticating: ${errorMessage}`);
        addOmniChannelLogEntry(`Error authenticating: ${errorMessage}`);
    };

    const onUserSessionReady = () => {
        addOmniChannelLogEntry('Received user session ready from Cention');
        setShouldAuthenticateToCention(false);
        const payload = getSubscribeToUserOwnedErrandsCountPayload();
        sendPostMessage(payload, centionUrl, iFrameRef);
    };

    const onCentionIsReadyToReceiveAuthenticationData = () => {
        addOmniChannelLogEntry(
            'Received cention ready to receive authentication data from Cention',
        );
        setShouldAuthenticateToCention(true);
    };

    const handleIncomingUserOwnedErrandsCountData = (
        data: OmniMessageEventData,
    ) => {
        const errandsCount = getErrandsCountFromData(data);
        if (!Number.isInteger(errandsCount)) {
            addOmniChannelLogEntry(
                'Received user owned errands count message from Cention, but errandsCount was not an integer: ',
                errandsCount,
            );
            return;
        }

        const logString = `Received user owned errands count message from Cention: ${errandsCount}`;
        addOmniChannelLogEntry(logString);
        dispatch(
            setUserOwnedOmnichannelErrandsAwaitingActivityCount(errandsCount),
        );
    };

    const handleIncomingMessageFromCentionClient = (
        event: MessageEvent<OmniMessageEventData>,
    ) => {
        //Used to handle postMessage triggers from Cention to us

        if (event.origin !== centionUrl) {
            return;
        }

        const eventData = event.data;

        if (dataIsOfTypeUserSessionReady(eventData)) {
            onUserSessionReady();
        } else if (dataIsOfTypeUserOwnedOmnichannelErrandsCount(eventData)) {
            handleIncomingUserOwnedErrandsCountData(eventData);
        } else if (
            dataIsOfTypeCentionIsReadyToReceiveAuthenticationData(eventData)
        ) {
            onCentionIsReadyToReceiveAuthenticationData();
        } else if (dataIsOfTypeAuthenticationError(eventData)) {
            onAuthenticationError(eventData);
        } else {
            addOmniChannelLogEntry(
                'Data received not constructed according to expectation, doing noting: ',
                eventData,
            );
        }
    };

    const onIframeLoaded = () => {
        setIFrameIsLoaded(true);
    };

    const onIframeRef = (element: HTMLIFrameElement) => {
        setIframeRef(element);
        dispatch(setOmnichannelIframeReference(element));
    };

    return (
        <iframe
            key={'omnichannel-iframe'}
            ref={onIframeRef}
            title={'agent-omni-wrapper'}
            className={'absolute h-full w-full'}
            src={`${centionUrl}?whitelabel=true`}
            onLoad={onIframeLoaded}
            allow={'camera;microphone;clipboard-read;clipboard-write'}
        />
    );
};

export default OmniiFrameWrapper;

const sendPostMessage = (
    payload: OmniPostMessagePayload,
    centionUrl: string,
    iFrameRef: HTMLIFrameElement,
) => {
    addOmniChannelLogEntry('Triggering postMessage with payload: ', payload);

    try {
        iFrameRef.contentWindow.postMessage(payload, centionUrl);
    } catch (error) {
        addOmniChannelLogEntry(
            `Failed sending postMessage with error: ${error}`,
        );
    }
};
